<template>
  <div class="notif-bell" ref="rootRef">
    <button class="notif-bell__trigger" @click="toggleOpen" aria-label="Notificaciones">
      <span class="mdi mdi-bell-outline"></span>
      <span v-if="unreadCount > 0" class="notif-bell__badge">{{ unreadCount > 99 ? '99+' : unreadCount }}</span>
    </button>

    <div v-if="open" class="notif-bell__dropdown">
      <div class="notif-bell__header">
        <span class="notif-bell__title">Notificaciones</span>
        <button
          v-if="unreadCount > 0"
          class="notif-bell__mark-all"
          @click="onMarkAllRead"
        >
          Marcar todas como leídas
        </button>
      </div>

      <div class="notif-bell__tabs">
        <button
          class="notif-bell__tab"
          :class="{ 'notif-bell__tab--active': activeTab === 'all' }"
          @click="activeTab = 'all'"
        >
          Todas
        </button>
        <button
          class="notif-bell__tab"
          :class="{ 'notif-bell__tab--active': activeTab === 'unread' }"
          @click="activeTab = 'unread'"
        >
          No leídas
        </button>
      </div>

      <div class="notif-bell__list">
        <div v-if="loading" class="notif-bell__empty">Cargando...</div>

        <div v-else-if="visibleGroups.length === 0" class="notif-bell__empty">
          <span class="mdi mdi-check-circle-outline notif-bell__empty-icon"></span>
          <span>Sin notificaciones{{ activeTab === 'unread' ? ' sin leer' : '' }}</span>
        </div>

        <template v-else>
          <div v-for="group in visibleGroups" :key="group.label" class="notif-bell__group">
            <span class="notif-bell__group-label">{{ group.label }}</span>
            <button
              v-for="item in group.items"
              :key="item.id"
              class="notif-bell__item"
              :class="{ 'notif-bell__item--unread': !item.readAt }"
              @click="onItemClick(item)"
            >
              <span class="mdi notif-bell__item-icon" :class="MODULE_ICONS[item.module]"></span>
              <span class="notif-bell__item-body">
                <span class="notif-bell__item-title">{{ item.title }}</span>
                <span v-if="item.detail" class="notif-bell__item-detail">{{ item.detail }}</span>
              </span>
              <span v-if="!item.readAt" class="notif-bell__item-dot"></span>
            </button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { getNotifications, markNotificationRead, markAllNotificationsRead } from '@/services/notifications'

const props = defineProps({
  parkId: { type: String, default: null },
})

const router = useRouter()

const open = ref(false)
const loading = ref(false)
const activeTab = ref('all')
const items = ref([])
const unreadCount = ref(0)
const rootRef = ref(null)

let pollTimer = null

const MODULE_ICONS = {
  contracts: 'mdi-file-document-outline',
  finance: 'mdi-cash-multiple',
  maintenance: 'mdi-wrench-outline',
  services: 'mdi-cog-outline',
}

async function loadNotifications() {
  if (!props.parkId) return
  try {
    loading.value = true
    const data = await getNotifications(props.parkId)
    items.value = data.items || []
    unreadCount.value = data.unreadCount || 0
  } catch (e) {
    // fallo silencioso: no interrumpimos el dashboard por notificaciones
  } finally {
    loading.value = false
  }
}

function toggleOpen() {
  open.value = !open.value
  if (open.value) loadNotifications()
}

function dayLabel(dateStr) {
  const date = new Date(dateStr)
  const now = new Date()
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
  const diffDays = Math.round((startOfDay(now) - startOfDay(date)) / (1000 * 60 * 60 * 24))
  if (diffDays === 0) return 'Hoy'
  if (diffDays === 1) return 'Ayer'
  return date.toLocaleDateString('es-MX', { day: 'numeric', month: 'long' })
}

const filteredItems = computed(() => {
  if (activeTab.value === 'unread') return items.value.filter((n) => !n.readAt)
  return items.value
})

const visibleGroups = computed(() => {
  const groups = []
  const byLabel = new Map()
  for (const item of filteredItems.value) {
    const label = dayLabel(item.createdAt)
    if (!byLabel.has(label)) {
      const group = { label, items: [] }
      byLabel.set(label, group)
      groups.push(group)
    }
    byLabel.get(label).items.push(item)
  }
  return groups
})

async function onItemClick(item) {
  if (!item.readAt) {
    try {
      await markNotificationRead(props.parkId, item.id)
      item.readAt = new Date().toISOString()
      unreadCount.value = Math.max(0, unreadCount.value - 1)
    } catch (e) {
      // ignoramos el error de marcado, no bloquea la navegación
    }
  }
  open.value = false
  if (item.link) router.push(item.link)
}

async function onMarkAllRead() {
  try {
    await markAllNotificationsRead(props.parkId)
    const now = new Date().toISOString()
    items.value = items.value.map((n) => (n.readAt ? n : { ...n, readAt: now }))
    unreadCount.value = 0
  } catch (e) {
    // fallo silencioso
  }
}

function onDocumentClick(e) {
  if (open.value && rootRef.value && !rootRef.value.contains(e.target)) {
    open.value = false
  }
}

onMounted(() => {
  loadNotifications()
  document.addEventListener('click', onDocumentClick)
  pollTimer = setInterval(loadNotifications, 15000)
})

onUnmounted(() => {
  document.removeEventListener('click', onDocumentClick)
  if (pollTimer) clearInterval(pollTimer)
})
</script>

<style scoped>
.notif-bell {
  position: relative;
  display: flex;
}

.notif-bell__trigger {
  position: relative;
  display: flex;
  align-items: center;
  background: none;
  border: none;
  color: var(--text-secondary, rgba(255, 255, 255, 0.6));
  font-size: 1.1rem;
  cursor: pointer;
  padding: 0;
}

.notif-bell__badge {
  position: absolute;
  top: -6px;
  right: -8px;
  background: var(--accent-red, #e84545);
  color: white;
  font-size: 0.6rem;
  font-family: var(--font-mono);
  padding: 0 4px;
  border-radius: 8px;
  min-width: 14px;
  text-align: center;
  line-height: 1.4;
}

.notif-bell__dropdown {
  position: absolute;
  top: calc(100% + 0.6rem);
  right: 0;
  width: 340px;
  max-height: 440px;
  display: flex;
  flex-direction: column;
  background: var(--bg-surface-1);
  border: 1px solid var(--border-active);
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  z-index: 50;
  overflow: hidden;
}

.notif-bell__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem 0.5rem;
}

.notif-bell__title {
  font-family: var(--font-body);
  font-weight: 600;
  font-size: 0.9rem;
  color: #1e3a5f;
}

.notif-bell__mark-all {
  background: none;
  border: none;
  color: var(--accent-blue);
  font-size: 0.7rem;
  cursor: pointer;
  padding: 0;
}

.notif-bell__mark-all:hover {
  text-decoration: underline;
}

.notif-bell__tabs {
  display: flex;
  gap: 0.4rem;
  padding: 0 1rem 0.5rem;
  border-bottom: 1px solid var(--border-subtle);
}

.notif-bell__tab {
  background: none;
  border: none;
  color: #64748b;
  font-size: 0.75rem;
  cursor: pointer;
  padding: 0.3rem 0.6rem;
  border-radius: 6px;
}

.notif-bell__tab--active {
  background: #eff6ff;
  color: #1e3a5f;
  font-weight: 600;
}

.notif-bell__list {
  overflow-y: auto;
  padding: 0.4rem 0;
}

.notif-bell__group {
  display: flex;
  flex-direction: column;
}

.notif-bell__group-label {
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #94a3b8;
  padding: 0.5rem 1rem 0.25rem;
}

.notif-bell__item {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem 1rem;
  transition: background 0.12s ease;
}

.notif-bell__item:hover {
  background: #f8fafc;
}

.notif-bell__item--unread .notif-bell__item-title {
  font-weight: 600;
}

.notif-bell__item-icon {
  color: #94a3b8;
  font-size: 0.95rem;
  margin-top: 0.15rem;
}

.notif-bell__item-body {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  flex: 1;
  min-width: 0;
}

.notif-bell__item-title {
  font-size: 0.75rem;
  color: #1e3a5f;
  line-height: 1.3;
}

.notif-bell__item-detail {
  font-size: 0.68rem;
  color: #64748b;
  line-height: 1.3;
}

.notif-bell__item-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--accent-blue);
  margin-top: 0.35rem;
  flex-shrink: 0;
}

.notif-bell__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;
  padding: 1.75rem 1rem;
  color: #64748b;
  font-size: 0.78rem;
  text-align: center;
}

.notif-bell__empty-icon {
  font-size: 1.4rem;
  color: var(--accent-green, #10b981);
}

.notif-bell__list::-webkit-scrollbar {
  width: 6px;
}

.notif-bell__list::-webkit-scrollbar-thumb {
  background: var(--border-active);
  border-radius: 3px;
}
</style>